import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import * as S from './styled';
import { checkKey, KEY } from '../../utils/inputEvents';
import Placeholder from '../Placeholder';

const normalizedCompare = (s1, s2) => {
  const nS1 = s1.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const nS2 = s2.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  return nS1.toUpperCase() === nS2.toUpperCase();
};

const Autocomplete = (
  {
    placeholder,
    options,
    setter,
    getOptionLabel,
    getOptionValue,
    value,
    validations,
    invalid,
    errorMessage,
    openMenuOnFocus = true,
    onInputChange,
    noAutoSelect,
    ...rest
  },
  ref
) => {
  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [shouldValidate, setShouldValidate] = useState(false);

  const autoSelect = () =>
    setter(() => ({
      invalid,
      value:
        getOptionValue(
          options.find(o => normalizedCompare(getOptionLabel(o), inputValue)) ||
            ''
        ) || value,
      errorMessage,
    }));

  const handleSelected = (option, { action }) => {
    if (action === 'select-option') {
      setter({
        invalid: false,
        errorMessage: '',
        value: getOptionValue(option),
      });
    }
  };
  const handleInputChange = (localValue, { action }) => {
    if (action === 'input-change' && !noAutoSelect) {
      setInputValue(localValue);
    }
    if (action === 'menu-close' && !noAutoSelect) {
      if (inputValue.length > 2) {
        autoSelect();
      }
      setInputValue('');
    }
    if (onInputChange) {
      onInputChange(localValue, action);
    }
  };

  const validate = useCallback(() => {
    const invalidRule = validations.find(validation =>
      validation.validate(value)
    );

    setter({
      invalid: !!invalidRule,
      errorMessage: invalidRule?.errorMessage,
      value,
    });
    return !!invalidRule;
  }, [validations, value, setter]);

  useEffect(() => {
    if (shouldValidate) {
      validate();
      setShouldValidate(false);
    }
  }, [shouldValidate, validate]);
  useImperativeHandle(ref, () => ({
    validate,
  }));

  return (
    <S.AutocompleteContainer>
      <S.Autocomplete
        invalid={invalid}
        placeholder=""
        options={options}
        onChange={handleSelected}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        closeMenuOnScroll
        openMenuOnFocus={openMenuOnFocus}
        classNamePrefix="autocomplete"
        isOptionSelected={() => false}
        noOptionsMessage={() => 'Nenhum item encontrado'}
        value={options.find(o => getOptionValue(o) === value)}
        onKeyDown={e => {
          if (value === '' && checkKey(e, KEY.TAB.code, KEY.TAB.name)) {
            e.preventDefault();
          }
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setShouldValidate(true);
        }}
        onInputChange={handleInputChange}
        {...rest}
      />
      <Placeholder
        className="placeholder"
        active={value || focused}
        invalid={invalid}
      >
        {placeholder}
      </Placeholder>
      {invalid && <S.ErrorMessage>{errorMessage}</S.ErrorMessage>}
    </S.AutocompleteContainer>
  );
};

export default forwardRef(Autocomplete);
